import { useState, useRef, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Button,
  TextField,
  Typography,
  Chip,
  IconButton,
  Box,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Pencil, Camera, Check, X } from "lucide-react";
import { getAccessToken, getUserData } from "../../../Helpers/authHelper";
import useApi from "../../../Hooks/useApi";
import { useAuth } from "../../../Context Api/AuthContext";

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const ProfileField = ({ label, value, children }) => (
  <Box mb={3}>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Paper
      variant="outlined"
      sx={{ p: 1.5, mt: 1, borderRadius: "10px", backgroundColor: "#F8F8F8" }}
    >
      {children || (
        <Typography
          color={value ? "text.primary" : "text.secondary"}
          fontStyle={!value ? "italic" : "normal"}
        >
          {value || "Not specified"}
        </Typography>
      )}
    </Paper>
  </Box>
);

const api = {
  updateUserName: async (request, namePayload) => {
    const response = await request("PUT", `/v1/user/username`, {
      data: namePayload,
    });
    return response;
  },
  getUserName: async (request) => {
    const access_token = getAccessToken();
    const userData = await request("GET", "/v1/user", {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
    return userData;
  },
  updateAvatar: async (request, formData) => {
    const response = await request("PUT", `/v1/user/profile-picture/upload`, {
      data: formData,
      headers: {
        "Content-Type": undefined,
      },
    });
    return response;
  },
  getProfilePicture: async (request) => {
    const access_token = getAccessToken();
    const response = await request("GET", "/v1/user/profile-picture", {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
      responseType: "blob",
    });
    return response;
  },
};

export default function ProfilePage() {
  const { updateUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
  });

  const [avatarUrl, setAvatarUrl] = useState("");
  const fileInputRef = useRef(null);
  const { request } = useApi();

  // New state for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user_data = await api.getUserName(request);
        if (user_data) {
          setUserData(user_data);
          setAvatarUrl(user_data.photo || "");
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [request]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const imageBlob = await api.getProfilePicture(request);
        const imageUrl = URL.createObjectURL(imageBlob);
        setAvatarUrl(imageUrl);
      } catch (imageError) {
        console.log("No profile picture found or failed to load it.");
        setAvatarUrl("");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [request]);

  const handleNameEdit = () => {
    const names = (userData?.fullName || "").split(" ").filter(Boolean);
    const nameParts = {
      firstName: names[0] || "",
      middleName: names.length > 2 ? names.slice(1, -1).join(" ") : "",
      lastName: names.length > 1 ? names[names.length - 1] : "",
    };
    setEditedName(nameParts);
    setIsEditingName(true);
  };

  const handleNameSave = async () => {
    if (!editedName.firstName.trim() || !editedName.lastName.trim()) {
      setFeedback({
        open: true,
        message: "First name and last name are required.",
        severity: "warning",
      });
      return;
    }
    setIsSaving(true);
    try {
      const updatedUser = await api.updateUserName(request, editedName);
      const getUserName = await api.getUserName(request);
      updateUser(getUserName);
      setUserData(getUserName);
      setIsEditingName(false);
      setFeedback({
        open: true,
        message: updatedUser?.message,
        severity: "success",
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: error?.response?.data?.message || "Could not update name.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleNameCancel = () => {
    setIsEditingName(false);
  };

  const handleNameChange = (e) => {
    const { name, value } = e.target;
    setEditedName((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;


    const MAX_FILE_SIZE = 2 * 1024 * 1024;


    if (file.size > MAX_FILE_SIZE) {
      setFeedback({
        open: true,
        message: "Image size must be 2MB or less.",
        severity: "error",
      });
   
      e.target.value = null;
      return;
    }

   
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setIsModalOpen(true);


    e.target.value = null;
  };

  const handleModalClose = () => {
    if (isSaving) return; 
    setIsModalOpen(false);
    setPreviewUrl("");
    setSelectedFile(null);
  };

  const handleSaveAvatar = async () => {
    if (!selectedFile) return;

    setIsSaving(true);
    setIsModalOpen(false); 

    try {
      const formData = new FormData();
      formData.append("image", selectedFile);

      await api.updateAvatar(request, formData);
      const user_data = getUserData();
      const imageBlob = await api.getProfilePicture(request);
      const imageUrl = URL.createObjectURL(imageBlob);

      // Update local state and context
      setAvatarUrl(imageUrl);
      updateUser({ ...user_data, photo: imageUrl });

      setFeedback({
        open: true,
        message: "Avatar updated successfully!",
        severity: "success",
      });
    } catch (error) {
      setFeedback({
        open: true,
        message: "Failed to upload avatar.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
      setPreviewUrl("");
      setSelectedFile(null);
    }
  };

  const handleCloseFeedback = () => {
    setFeedback((prev) => ({ ...prev, open: false }));
  };

  const roleList = userData?.roles
    ? userData.roles.split(",").map((role) => role.trim())
    : [];

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!userData) {
    return <Typography>User data could not be loaded.</Typography>;
  }

  return (
    <Box sx={{ bgcolor: "background.default", p: 4 }}>
      <Box maxWidth="lg" mx="auto" className="text-start">
        <Card sx={{ overflow: "hidden", boxShadow: 2 }}>
          <CardHeader
            sx={{ borderBottom: "1px solid", borderColor: "divider" }}
            className="bg-[#7ddbff] bg-opacity-[30%]"
            title={
              <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems="center"
                gap={3}
              >
                <Box position="relative">
                  <IconButton
                    onClick={handleAvatarClick}
                    sx={{ p: 0, borderRadius: "50%" }}
                    disabled={isSaving}
                  >
                    <Avatar
                      src={avatarUrl}
                      sx={{ width: 120, height: 120, fontSize: 36 }}
                    >
                      {getInitials(userData.fullName)}
                    </Avatar>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      {isSaving ? (
                        <CircularProgress size={28} color="inherit" />
                      ) : (
                        <Camera size={28} />
                      )}
                    </Box>
                  </IconButton>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    style={{ display: "none" }}
                  />
                </Box>

                <Box flex={1} textAlign={{ xs: "center", sm: "left" }}>
                  {isEditingName ? (
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box
                        display="flex"
                        flexDirection={{ xs: "column", sm: "row" }}
                        gap={1.5}
                      >
                        <TextField
                          name="firstName"
                          label="First Name"
                          value={editedName.firstName}
                          onChange={handleNameChange}
                          autoFocus
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={isSaving}
                          required
                        />
                        <TextField
                          name="middleName"
                          label="Middle Name"
                          value={editedName.middleName}
                          onChange={handleNameChange}
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={isSaving}
                        />
                        <TextField
                          name="lastName"
                          label="Last Name"
                          value={editedName.lastName}
                          onChange={handleNameChange}
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={isSaving}
                          required
                        />
                      </Box>
                      <Box display="flex" gap={2}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleNameSave}
                          startIcon={
                            isSaving ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <Check size={16} />
                            )
                          }
                          disabled={isSaving}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleNameCancel}
                          startIcon={<X size={16} />}
                          disabled={isSaving}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent={{ xs: "center", sm: "flex-start" }}
                        gap={1}
                      >
                        <Typography variant="h5" fontWeight="bold">
                          {userData?.fullName}
                        </Typography>
                        <IconButton
                          onClick={handleNameEdit}
                          size="small"
                          aria-label="Edit name"
                        >
                          <Pencil size={16} />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Click the pencil icon to edit your name
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            }
          />

          <CardContent>
            <ProfileField label="Email Address" value={userData.email} />
            <ProfileField label="Department" value={userData.department} />
            <ProfileField label="Designation" value={userData.designation} />
            <ProfileField label="Role">
              <Box display="flex" gap={1} flexWrap="wrap">
                {roleList.length > 0 ? (
                  roleList.map((role, index) => (
                    <Chip
                      key={index}
                      label={role}
                      color="primary"
                      variant="outlined"
                    />
                  ))
                ) : (
                  <Typography color="text.secondary" fontStyle="italic">
                    Not specified
                  </Typography>
                )}
              </Box>
            </ProfileField>
          </CardContent>
        </Card>
      </Box>

      {/* --- Profile Picture Upload Modal --- */}
      <Dialog
        open={isModalOpen}
        onClose={handleModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "center" }}>
          Your New Profile Picture
        </DialogTitle>
        <DialogContent sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <Avatar src={previewUrl} sx={{ width: 250, height: 250 }} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleModalClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAvatar}
            variant="contained"
            disabled={isSaving}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}